import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Icon,
  Badge,
  SimpleGrid,
  Flex,
  Progress,
  List,
  ListItem,
  ListIcon,
  Image,
} from "@chakra-ui/react";
import {
  FaCheckCircle,
  FaArrowRight,
  FaBatteryFull,
  FaSolarPanel,
  FaShieldAlt,
  FaDollarSign,
  FaStar,
  FaPhone,
  FaExclamationTriangle,
  FaMapMarkerAlt,
  FaHome,
  FaLeaf,
  FaBolt,
  FaTimesCircle,
} from "react-icons/fa";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SOLAR_GOLD = "#C69214";
const SOLAR_GOLD_HOVER = "#a07e10";
const NAVY = "#141C49";

type QuizAnswer = string | null;

interface QuizStep {
  question: string;
  options: { label: string; icon: React.ElementType; value: string }[];
}

const quizSteps: QuizStep[] = [
  {
    question: "Do you already have solar panels installed on your home?",
    options: [
      { label: "Yes — I already have solar", icon: FaSolarPanel, value: "yes-solar" },
      { label: "No — not yet", icon: FaTimesCircle, value: "no-solar" },
    ],
  },
  {
    question: "Do you own your home?",
    options: [
      { label: "Yes — I own my home", icon: FaHome, value: "owner" },
      { label: "No — I rent", icon: FaTimesCircle, value: "renter" },
    ],
  },
  {
    question: "What is your average quarterly electricity bill?",
    options: [
      { label: "Under $200", icon: FaDollarSign, value: "low" },
      { label: "$200 – $600", icon: FaDollarSign, value: "medium" },
      { label: "Over $600", icon: FaDollarSign, value: "high" },
    ],
  },
  {
    question: "Where is your property located?",
    options: [
      { label: "Queensland", icon: FaMapMarkerAlt, value: "qld" },
      { label: "NSW / VIC / SA", icon: FaMapMarkerAlt, value: "other-state" },
      { label: "WA / NT / TAS", icon: FaMapMarkerAlt, value: "other-state-2" },
    ],
  },
];

const QuizSection: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswer[]>([null, null, null, null]);
  const [completed, setCompleted] = useState(false);

  const progress = ((step) / quizSteps.length) * 100;
  const current = quizSteps[step];

  const handleAnswer = (value: string) => {
    if (value === "renter") {
      navigate("/solar/battery-rebates/no-qualify");
      return;
    }
    const updated = [...answers];
    updated[step] = value;
    setAnswers(updated);
    if (step < quizSteps.length - 1) {
      setStep(step + 1);
    } else {
      setCompleted(true);
    }
  };

  if (completed) {
    return (
      <VStack spacing={6} textAlign="center">
        <Icon as={FaCheckCircle} boxSize={14} color={SOLAR_GOLD} />
        <Heading size="lg" color={NAVY}>
          Great news — your home likely qualifies!
        </Heading>
        <Text color="gray.600" fontSize="md" maxW="lg">
          Based on your answers, you may be eligible for up to{" "}
          <Text as="span" fontWeight="800" color={SOLAR_GOLD}>
            30% off
          </Text>{" "}
          battery installation through current government rebate programs. Secure your
          assessment before funding runs out.
        </Text>
        <HStack spacing={4} pt={2} flexWrap="wrap" justify="center">
          <Button
            as={RouterLink}
            to="/solar/quote"
            size="lg"
            bg={SOLAR_GOLD}
            color="white"
            _hover={{ bg: SOLAR_GOLD_HOVER }}
            rightIcon={<FaArrowRight />}
            px={8}
          >
            Claim My Free Assessment
          </Button>
          <Button
            as="a"
            href="tel:1300663765"
            size="lg"
            bg={NAVY}
            color="white"
            _hover={{ bg: "#0d1235" }}
            leftIcon={<FaPhone />}
            px={8}
          >
            Call 1300 663 765
          </Button>
        </HStack>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} w="full">
      <Flex w="full" justify="space-between" align="center">
        <Text fontSize="sm" color="gray.500" fontWeight="600">
          Question {step + 1} of {quizSteps.length}
        </Text>
        <Text fontSize="sm" color={SOLAR_GOLD} fontWeight="700">
          ~45 seconds to complete
        </Text>
      </Flex>
      <Progress
        value={progress}
        w="full"
        borderRadius="full"
        size="sm"
        colorScheme="yellow"
        bg="gray.100"
      />
      <Heading size="md" color={NAVY} textAlign="center" pt={2}>
        {current.question}
      </Heading>
      <SimpleGrid columns={{ base: 1, md: current.options.length > 2 ? 3 : 2 }} spacing={4} w="full" pt={2}>
        {current.options.map((opt) => (
          <Button
            key={opt.value}
            onClick={() => handleAnswer(opt.value)}
            variant="outline"
            borderColor="gray.200"
            bg="white"
            color={NAVY}
            fontWeight="600"
            fontSize="sm"
            h="auto"
            py={5}
            px={6}
            borderRadius="xl"
            leftIcon={<Icon as={opt.icon} color={SOLAR_GOLD} />}
            _hover={{
              borderColor: SOLAR_GOLD,
              bg: `${SOLAR_GOLD}10`,
              transform: "translateY(-2px)",
              boxShadow: "md",
            }}
            transition="all 0.2s"
            whiteSpace="normal"
            textAlign="left"
            justifyContent="flex-start"
          >
            {opt.label}
          </Button>
        ))}
      </SimpleGrid>
    </VStack>
  );
};

const SolarSavingsBatteryRebates: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/battery-rebates"
        fallbackTitle="Solar Savings & Battery Rebates Ending Soon | One Solar Australia"
        fallbackDescription="Battery rebate funding is capped and ending soon. Check if your Queensland home qualifies for up to 30% off installation — free, no-obligation assessment from One Solar Australia."
      />
      <NavbarWithCallToAction />

      {/* ── Urgency Banner ── */}
      <Box bg={SOLAR_GOLD} py={3}>
        <Container maxW="container.xl">
          <HStack justify="center" spacing={3} flexWrap="wrap">
            <Icon as={FaExclamationTriangle} color="white" boxSize={4} />
            <Text color="white" fontWeight="700" fontSize={{ base: "sm", md: "md" }} textAlign="center">
              Battery rebate funding is capped — check now before it runs out
            </Text>
            <Icon as={FaExclamationTriangle} color="white" boxSize={4} />
          </HStack>
        </Container>
      </Box>

      {/* ── Hero ── */}
      <Box
        position="relative"
        color="white"
        py={{ base: 20, md: 28 }}
        backgroundImage="url('/assets/solar-assets/asset%2037.jpeg')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(20, 28, 73, 0.82)",
          zIndex: 0,
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center">
            <Badge
              bg={SOLAR_GOLD}
              color="white"
              px={4} py={2}
              borderRadius="full"
              fontSize="sm"
              textTransform="uppercase"
              letterSpacing="wider"
              boxShadow="0 2px 8px rgba(198,146,20,0.5)"
            >
              Battery Rebates Ending Soon
            </Badge>
            <Heading size="3xl" fontWeight="black" maxW="4xl" lineHeight="1.15">
              Check If Your Home Qualifies for the New Battery Rebate
            </Heading>
            <Heading size="xl" color={SOLAR_GOLD} fontWeight="800">
              Up to 30% Off Installation
            </Heading>
            <Text fontSize="lg" color="gray.300" maxW="2xl" lineHeight="1.8">
              Licensed providers offering free, no-obligation assessments. Rebate funding is
              capped — act now to secure your savings before it ends.
            </Text>
            <HStack spacing={4} pt={2} flexWrap="wrap" justify="center">
              <HStack spacing={2}>
                <Icon as={FaCheckCircle} color={SOLAR_GOLD} boxSize={4} />
                <Text fontSize="sm" color="gray.300">Qualified, Experienced Installers</Text>
              </HStack>
              <HStack spacing={2}>
                <Icon as={FaCheckCircle} color={SOLAR_GOLD} boxSize={4} />
                <Text fontSize="sm" color="gray.300">Up to 25-Year Warranty</Text>
              </HStack>
              <HStack spacing={2}>
                <Icon as={FaStar} color={SOLAR_GOLD} boxSize={4} />
                <Text fontSize="sm" color="gray.300">4.7★ Google Reviews</Text>
              </HStack>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* ── Quiz CTA ── */}
      <Box bg="gray.50" py={{ base: 14, md: 20 }}>
        <Container maxW="container.md">
          <VStack spacing={8}>
            <VStack spacing={3} textAlign="center">
              <Badge bg={SOLAR_GOLD} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
                Find Out in 45 Seconds
              </Badge>
              <Heading size="xl" color={NAVY}>
                Does Your Home Qualify?
              </Heading>
              <Text color="gray.600" fontSize="md">
                Answer 4 quick questions to see if you're eligible for current battery rebate programs.
              </Text>
            </VStack>
            <Box
              w="full"
              bg="white"
              borderRadius="2xl"
              p={{ base: 6, md: 10 }}
              boxShadow="xl"
              border="2px solid"
              borderColor={`${SOLAR_GOLD}40`}
            >
              <QuizSection />
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* ── Stats Strip ── */}
      <Box
        position="relative"
        color="white"
        py={16}
        backgroundImage="url('/assets/solar-assets/asset%2020.jpeg')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(20, 20, 20, 0.75)",
          zIndex: 0,
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
            {[
              { value: "30%", label: "Off Installation via Rebate" },
              { value: "$50k", label: "Avg Savings Over 25 Years" },
              { value: "25yr", label: "Battery & Panel Warranty" },
              { value: "4.7★", label: "Google Reviews" },
            ].map((s) => (
              <VStack key={s.label} spacing={2} textAlign="center">
                <Heading size="2xl" color={SOLAR_GOLD} textShadow="0 2px 8px rgba(0,0,0,0.4)">
                  {s.value}
                </Heading>
                <Text fontSize="sm" color="rgba(255,255,255,0.9)" textAlign="center">
                  {s.label}
                </Text>
              </VStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Real Results Section ── */}
      <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
        <VStack spacing={4} mb={12} textAlign="center">
          <Badge bg={SOLAR_GOLD} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
            Real Results from Real Clients
          </Badge>
          <Heading size="xl" color={NAVY}>
            See What Australians Are Saving
          </Heading>
          <Text fontSize="lg" color="gray.600" maxW="2xl">
            Our team has helped hundreds of Australians dramatically cut their power bills with solar batteries.
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} alignItems="center">
          {/* Testimonial Card */}
          <Box
            bg="white"
            borderRadius="2xl"
            p={8}
            boxShadow="xl"
            border="2px solid"
            borderColor={`${SOLAR_GOLD}30`}
            position="relative"
          >
            <Box
              position="absolute"
              top={-4}
              left={8}
              bg={SOLAR_GOLD}
              color="white"
              px={4}
              py={1}
              borderRadius="full"
              fontSize="xs"
              fontWeight="700"
              textTransform="uppercase"
              letterSpacing="wider"
            >
              Featured Client
            </Box>
            <VStack align="flex-start" spacing={4} pt={4}>
              <HStack spacing={1}>
                {[1,2,3,4,5].map((i) => (
                  <Icon key={i} as={FaStar} color={SOLAR_GOLD} boxSize={4} />
                ))}
              </HStack>
              <Text color="gray.700" fontSize="lg" lineHeight="1.8" fontStyle="italic">
                "Fast install, clear communication and our evening bills dropped immediately. We
                genuinely couldn't believe the difference — I wish we'd done it sooner."
              </Text>
              <HStack spacing={3}>
                <Box
                  w={10} h={10}
                  bg={NAVY}
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  color="white"
                  fontWeight="800"
                  fontSize="md"
                >
                  R
                </Box>
                <VStack align="flex-start" spacing={0}>
                  <Text fontWeight="700" color={NAVY} fontSize="sm">Rachel S.</Text>
                  <Text color="gray.500" fontSize="xs">Gold Coast, QLD — February 2025</Text>
                </VStack>
              </HStack>

              {/* Case Study Stats */}
              <Box w="full" bg="gray.50" borderRadius="xl" p={5} mt={2}>
                <Text fontSize="xs" fontWeight="700" color={SOLAR_GOLD} textTransform="uppercase" letterSpacing="wider" mb={3}>
                  Case Study Details
                </Text>
                <SimpleGrid columns={3} spacing={4}>
                  {[
                    { label: "System", value: "10kWh Battery" },
                    { label: "Bill Before", value: "$297/qtr" },
                    { label: "Bill After", value: "$12/qtr" },
                  ].map((d) => (
                    <VStack key={d.label} spacing={1} textAlign="center">
                      <Text fontWeight="800" color={NAVY} fontSize="lg">{d.value}</Text>
                      <Text color="gray.500" fontSize="xs">{d.label}</Text>
                    </VStack>
                  ))}
                </SimpleGrid>
                <Flex mt={4} p={3} bg={NAVY} borderRadius="lg" justify="space-between" align="center">
                  <Text fontSize="sm" color="white" fontWeight="600">Projected 25-Year Savings</Text>
                  <Text fontSize="lg" fontWeight="900" color={SOLAR_GOLD}>$50,000+</Text>
                </Flex>
              </Box>
            </VStack>
          </Box>

          {/* Why Add a Battery */}
          <VStack align="flex-start" spacing={6}>
            <Badge bg={SOLAR_GOLD} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
              Why Add a Battery?
            </Badge>
            <Heading size="xl" color={NAVY} lineHeight="1.2">
              Your Solar Panels Work 9 to 5. Your Battery Works 24/7.
            </Heading>
            <Text color="gray.600" fontSize="md" lineHeight="1.8">
              Without a battery, any solar energy you don't use immediately gets exported back to the
              grid — often at a low feed-in rate. A battery lets you store that energy and use it at
              night, during peak pricing, or when the grid goes down.
            </Text>
            <List spacing={4} pt={2}>
              {[
                { icon: FaBolt, text: "Use stored clean power at night — no more peak-rate surprises" },
                { icon: FaShieldAlt, text: "Automatic backup protection during grid outages" },
                { icon: FaDollarSign, text: "Avoid expensive peak electricity rates every day" },
                { icon: FaLeaf, text: "Run your home on 100% clean energy, around the clock" },
                { icon: FaBatteryFull, text: "Earn up to 45c/kWh through Virtual Power Plant programs" },
              ].map((item) => (
                <ListItem key={item.text} display="flex" alignItems="flex-start" gap={3}>
                  <ListIcon as={item.icon} color={SOLAR_GOLD} mt="3px" flexShrink={0} boxSize={4} />
                  <Text fontSize="sm" color="gray.700" lineHeight="1.7">{item.text}</Text>
                </ListItem>
              ))}
            </List>
            <Button
              as={RouterLink}
              to="/solar/quote"
              bg={SOLAR_GOLD}
              color="white"
              size="lg"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: SOLAR_GOLD_HOVER }}
              transition="all 0.2s"
            >
              Get My Free Assessment
            </Button>
          </VStack>
        </SimpleGrid>
      </Container>

      {/* ── How the Rebate Works ── */}
      <Box bg="gray.50" py={{ base: 16, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={14} textAlign="center">
            <Badge bg={SOLAR_GOLD} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
              How It Works
            </Badge>
            <Heading size="xl" color={NAVY}>Three Steps to Your Rebate</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              We handle the paperwork and make the entire process simple, from assessment to installation.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            {[
              {
                step: "01",
                icon: FaCheckCircle,
                title: "Check Your Eligibility",
                body: "Complete our quick 45-second quiz above, or call us directly. We'll confirm whether your property qualifies for current rebate programs — no obligation.",
              },
              {
                step: "02",
                icon: FaHome,
                title: "Free Home Assessment",
                body: "A CEC-accredited installer visits your property, assesses your existing system (if any), and provides a detailed quote — including the rebate applied upfront.",
              },
              {
                step: "03",
                icon: FaBatteryFull,
                title: "Installation & Savings",
                body: "We install your battery system in a single day. You start saving immediately — on your evening bills, during peak periods, and through VPP feed-in earnings.",
              },
            ].map((item) => (
              <Box
                key={item.step}
                bg="white"
                borderRadius="2xl"
                p={8}
                boxShadow="md"
                border="1px solid"
                borderColor="gray.100"
                _hover={{ boxShadow: "xl", borderColor: SOLAR_GOLD }}
                transition="all 0.3s"
              >
                <Box
                  w={12} h={12}
                  bg={SOLAR_GOLD}
                  borderRadius="full"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontWeight="black"
                  fontSize="lg"
                  color="white"
                  mb={5}
                  boxShadow="0 2px 8px rgba(198,146,20,0.4)"
                >
                  {item.step}
                </Box>
                <Icon as={item.icon} boxSize={8} color={SOLAR_GOLD} mb={4} />
                <Heading size="md" color={NAVY} mb={3}>{item.title}</Heading>
                <Text color="gray.600" fontSize="sm" lineHeight="1.8">{item.body}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Trusted Installer Section ── */}
      <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="center">
          <Box borderRadius="2xl" overflow="hidden" h={{ base: "300px", md: "460px" }} position="relative" boxShadow="2xl">
            <Box
              as="img"
              src="/assets/solar-assets/asset%2033.jpeg"
              alt="CEC accredited solar installer on rooftop"
              w="full" h="full"
              objectFit="cover"
            />
            <Box
              position="absolute" bottom={0} left={0} right={0} p={5}
              bgGradient={`linear(to-t, ${NAVY}DD, transparent)`}
            >
              <Text fontSize="sm" fontWeight="700" color="white" letterSpacing="wider" textTransform="uppercase">
                CEC Accredited — Lic. 88686
              </Text>
            </Box>
          </Box>
          <VStack align="flex-start" spacing={6}>
            <Badge bg={SOLAR_GOLD} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
              Why One Solar Australia
            </Badge>
            <Heading size="xl" color={NAVY} lineHeight="1.2">
              Trusted by Hundreds of Queensland Homeowners
            </Heading>
            <Text color="gray.600" fontSize="lg" lineHeight="1.8">
              We've helped many Australians cut their power bills through smart solar and battery
              installations. Our team only installs quality systems backed by long-term manufacturer
              warranties and carries full CEC accreditation.
            </Text>
            <SimpleGrid columns={2} spacing={4} w="full">
              {[
                { label: "CEC Accredited", sub: "Lic. 88686" },
                { label: "4.7★ Reviews", sub: "Google Verified" },
                { label: "25-Year Warranty", sub: "Panels & Battery" },
                { label: "Same-Day Install", sub: "Most Installations" },
              ].map((f) => (
                <Box
                  key={f.label}
                  p={4}
                  bg="gray.50"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="gray.100"
                  _hover={{ borderColor: SOLAR_GOLD }}
                  transition="all 0.2s"
                >
                  <Text fontWeight="700" color={NAVY} fontSize="sm">{f.label}</Text>
                  <Text color="gray.500" fontSize="xs" mt={0.5}>{f.sub}</Text>
                </Box>
              ))}
            </SimpleGrid>
            <Text color="gray.500" fontSize="xs">
              * Battery rebate programs are subject to government funding availability and eligibility requirements.
              Your advisor will confirm applicable rebates at assessment.
            </Text>
          </VStack>
        </SimpleGrid>
      </Container>

      {/* ── Battery Brand Logos ── */}
      <Box bg="gray.50" py={14}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={10} textAlign="center">
            <Heading size="lg" color={NAVY}>Battery Brands We Install</Heading>
            <Text color="gray.600" maxW="xl">
              We only install battery systems from manufacturers with proven performance and long-term warranty support.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={6} alignItems="center">
            {[
              { img: "/assets/solar-assets/asset%2026.png", alt: "LG Chem" },
              { img: "/assets/solar-assets/asset%2022.png", alt: "Sungrow" },
              { img: "/assets/solar-assets/asset%2023.png", alt: "Solar Analytics" },
              { img: "/assets/solar-assets/asset%2024.png", alt: "Risen Solar Technology" },
              { img: "/assets/solar-assets/asset%2025.png", alt: "Q Cells" },
              { img: "/assets/solar-assets/asset%2027.png", alt: "Fronius" },
            ].map((b) => (
              <Box
                key={b.alt}
                p={4}
                bg="white"
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.200"
                _hover={{ borderColor: SOLAR_GOLD, boxShadow: "md" }}
                transition="all 0.3s"
                display="flex"
                alignItems="center"
                justifyContent="center"
                h="80px"
              >
                <Image src={b.img} alt={b.alt} maxH="44px" maxW="130px" objectFit="contain" />
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Final CTA ── */}
      <Box
        position="relative"
        py={{ base: 20, md: 28 }}
        backgroundImage="url('/assets/solar-assets/asset%2013.jpeg')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: `rgba(20, 28, 73, 0.90)`,
          zIndex: 0,
        }}
      >
        <Container maxW="container.lg" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center" color="white">
            <Badge
              bg={SOLAR_GOLD}
              color="white"
              px={4} py={2}
              borderRadius="full"
              fontSize="sm"
              textTransform="uppercase"
              letterSpacing="wider"
              boxShadow="0 2px 8px rgba(198,146,20,0.5)"
            >
              Rebate Funding Is Capped
            </Badge>
            <Heading size="2xl" fontWeight="black" maxW="3xl" lineHeight="1.2">
              See How Much You Can Save With Solar + Battery
            </Heading>
            <Text fontSize="lg" color="gray.300" maxW="2xl" lineHeight="1.8">
              Don't wait until the funding runs out. Book your free, no-obligation assessment
              today and lock in your rebate savings.
            </Text>
            <HStack spacing={4} pt={4} flexWrap="wrap" justify="center">
              <Button
                as={RouterLink}
                to="/solar/quote"
                size="lg"
                bg={SOLAR_GOLD}
                color="white"
                _hover={{ bg: SOLAR_GOLD_HOVER }}
                rightIcon={<FaArrowRight />}
                px={10}
                transition="all 0.2s"
              >
                Get My Free Assessment
              </Button>
              <Button
                as="a"
                href="tel:1300663765"
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: SOLAR_GOLD, borderColor: SOLAR_GOLD }}
                leftIcon={<FaPhone />}
                px={8}
                transition="all 0.2s"
              >
                1300 663 765
              </Button>
            </HStack>
            <Text fontSize="xs" color="gray.500" pt={2}>
              Free assessment. No obligation. CEC accredited installers. ACN 642 690 435
            </Text>
          </VStack>
        </Container>
      </Box>

      <SolarFooter />
    </Box>
  );
};

export default SolarSavingsBatteryRebates;
